import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { AddMessageMutation as AddMessage } from '__generated__/graphql';
import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import AddMessageMutation from './AddMessageMutation';
import EventPicker from '../../Events/EventPicker';
import UI from '../../../common/UI';
import useMutation from '../../../api/useMutation';
import useValidation from '../../../common/useValidation';

type AddedMessage = AddMessage['addMessage'];

const addMessageSchema = yup.object().shape({
  event: yup.object({
    id: yup.string().required(),
  }).required(),
  title: yup.string().ensure().required(),
});

export interface AddMessageFormProps {
  eventId?: string;
  onSuccess?: (addedMessage: AddedMessage) => void;
}

const AddMessageForm = ({ eventId, onSuccess }: AddMessageFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      event: eventId ? { id: eventId } : null,
      title: '',
    },
  });

  const [addMessage, { loading, error }] = useMutation(AddMessageMutation, {
    variables: {
      input: form.values,
    },
    onCompleted: ({ addMessage }) => {
      notifier.success(t('notifications.message_added'));

      onSuccess?.(addMessage);
    },
  });

  const { errors, valid } = useValidation({
    schema: addMessageSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addMessage()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>{t('add_a_message')}</UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_message_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !errors.event, touched: form.touched.event }}>
          <UI.InputLabel>
            {t('event')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <EventPicker
            eventId={form.values.event?.id}
            onChange={(event) => form.set('event', event ? { id: event.id } : null)}
            nullable={false}
          />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="MessageTitle">
            {t('title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {t('message_title_description')}
          </UI.InputDescription>
          <UI.Input {...form.text('title')} id="MessageTitle" />
          <UI.ErrorMessages attribute={t('title')} errors={errors.title} />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('add_message')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default AddMessageForm;
